import { parseISO } from 'date-fns';

import { convertTo12HourFormat } from '~shared/utils/date-time';

export const TIMELINE_WINDOW_START_HOUR = 6;
export const TIMELINE_WINDOW_HOURS = 24;
export const TIMELINE_WINDOW_MINUTES = TIMELINE_WINDOW_HOURS * 60;
export const TIMELINE_WINDOW_START_MINUTES = TIMELINE_WINDOW_START_HOUR * 60;

/** Clock minutes (0-1439) → offset from 6 AM on the timeline (0-1439). */
export function toTimelineStartOffset(clockMinutes: number): number {
  if (clockMinutes >= TIMELINE_WINDOW_START_MINUTES) {
    return clockMinutes - TIMELINE_WINDOW_START_MINUTES;
  }

  return clockMinutes + (24 * 60 - TIMELINE_WINDOW_START_MINUTES);
}

/** End offset; handles end at 6 AM and overnight spans. */
export function toTimelineEndOffset(
  endClockMinutes: number,
  startClockMinutes: number,
): number {
  const startOffset = toTimelineStartOffset(startClockMinutes);

  if (endClockMinutes === TIMELINE_WINDOW_START_MINUTES) {
    return TIMELINE_WINDOW_MINUTES;
  }

  const endOffset = toTimelineStartOffset(endClockMinutes);

  if (endClockMinutes < startClockMinutes) {
    return endOffset;
  }

  if (endOffset <= startOffset) {
    return TIMELINE_WINDOW_MINUTES;
  }

  return endOffset;
}

export function hasValidTimelineDuration(
  startClockMinutes: number,
  endClockMinutes: number,
): boolean {
  const startOffset = toTimelineStartOffset(startClockMinutes);
  const endOffset = toTimelineEndOffset(endClockMinutes, startClockMinutes);
  return endOffset > startOffset;
}

export function getTimelineWindowHourLabels(): Array<number> {
  return Array.from(
    { length: TIMELINE_WINDOW_HOURS },
    (_, index) => (TIMELINE_WINDOW_START_HOUR + index) % 24,
  );
}

export function formatClockMinutesTo12Hour(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  const time24 = `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
  return convertTo12HourFormat(time24);
}

/**
 * Parse a class time string into minutes from midnight (local time).
 * Handles ISO datetimes, locale strings ("Apr 23, 2026, 10:00:00 AM"), and HH:mm.
 */
export function parseTimeToMinutes(timeStr: string): number {
  if (!timeStr) return 0;

  const trimmed = timeStr.trim();

  if (trimmed.includes('T') || /^\d{4}-\d{2}-\d{2}/.test(trimmed)) {
    const date = parseISO(trimmed);
    if (!Number.isNaN(date.getTime())) {
      return date.getHours() * 60 + date.getMinutes();
    }

    const fallbackDate = new Date(trimmed);
    if (!Number.isNaN(fallbackDate.getTime())) {
      return fallbackDate.getHours() * 60 + fallbackDate.getMinutes();
    }
  }

  if (/,\s*\d{4}/.test(trimmed) || /\b(AM|PM)\b/i.test(trimmed)) {
    const date = new Date(trimmed);
    if (!Number.isNaN(date.getTime())) {
      return date.getHours() * 60 + date.getMinutes();
    }
  }

  const ampmMatch = /^(\d{1,2}):(\d{2})(?::\d{2})?\s*(AM|PM)$/i.exec(trimmed);
  if (ampmMatch) {
    let hours = parseInt(ampmMatch[1] ?? '0', 10);
    const minutes = parseInt(ampmMatch[2] ?? '0', 10);
    const period = ampmMatch[3]?.toUpperCase();

    if (period === 'PM' && hours !== 12) hours += 12;
    if (period === 'AM' && hours === 12) hours = 0;

    return hours * 60 + minutes;
  }

  const hmMatch = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/.exec(trimmed);
  if (hmMatch) {
    const hours = parseInt(hmMatch[1] ?? '0', 10);
    const minutes = parseInt(hmMatch[2] ?? '0', 10);
    if (!Number.isNaN(hours) && !Number.isNaN(minutes)) {
      return hours * 60 + minutes;
    }
  }

  return 0;
}

export const TIMELINE_HOUR_WIDTH_PX = 200;
export const TIMELINE_WIDTH_PX = TIMELINE_WINDOW_HOURS * TIMELINE_HOUR_WIDTH_PX;
/** Tall enough for private-lesson blocks (title + time + student + instructor). */
export const TIMELINE_LANE_HEIGHT_PX = 88;
export const TIMELINE_BLOCK_INSET_PX = 2;
export const ROOM_COLUMN_WIDTH_PX = 180;
export const TIMELINE_MIN_BLOCK_WIDTH_PX = 80;
/** Minimum width for classes lasting 30 minutes or less (when space allows). */
export const TIMELINE_MIN_SHORT_BLOCK_WIDTH_PX = 100;
export const TIMELINE_SHORT_CLASS_MAX_MINUTES = 30;
export const TIMELINE_HEADER_HEIGHT_PX = 48;
